/* eslint-disable react/prop-types */

/* eslint-disable react/no-danger */

/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import isNumber from 'lodash/isNumber';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

// import * as AppPropTypes from '../../lib/PropTypes';
import { useResizeObserver } from '../../hooks/useObserver';

import { useWindowSize } from '../../contexts/SiteContext';
import { useFloatingVideo } from '../../contexts/VideoContext';
import Detector from './Detector';

import styles from '../../../styles/partials/floating-video-slot.module.scss';

const propTypes = {
    className: PropTypes.string,
    name: PropTypes.string,
    fixed: PropTypes.bool,
    adaptative: PropTypes.bool,
    fullWidth: PropTypes.bool,
    fallback: PropTypes.bool,
    debug: PropTypes.bool,
};

const defaultProps = {
    className: null,
    name: 'slot',
    fixed: false,
    adaptative: false,
    fullWidth: false,
    fallback: false,
    debug: false,
};

const FloatingVideoSlot = ({ className, name, fallback, fixed, adaptative, fullWidth, debug }) => {
    const {
        floatingVideo,
        addFloatingVideoSlot,
        removeFloatingVideoSlot,
        updateFloatingVideoSlot,
        maxVideoWidth,
    } = useFloatingVideo();

    const [refresh, setRefresh] = useState(0);
    const id = useMemo(() => `${name}-${uuid()}`, [name]);

    // Keep the box size list here
    const {
        ref,
        entry: { contentRect = null },
    } = useResizeObserver();

    const { width: windowWidth = 0, height: windowHeight = 0 } = useWindowSize();

    useEffect(() => {
        const boundingRect = ref.current.getBoundingClientRect();
        const ratio =
            boundingRect.width > 0 && boundingRect.height
                ? boundingRect.height / boundingRect.width
                : 1;
        const xScrollOffset = !fixed ? window.scrollX || window.pageXOffset : 0;
        const yScrollOffset = !fixed ? window.scrollY || window.pageYOffset : 0;
        const rect = {
            x: boundingRect.left + xScrollOffset,
            y: boundingRect.top + yScrollOffset,
            width: boundingRect.width,
            height: boundingRect.height,
            scale: boundingRect.width / (maxVideoWidth || 1),
            ratio,
        };
        updateFloatingVideoSlot(id, { rect });
    }, [
        refresh,
        windowWidth,
        windowHeight,
        id,
        contentRect,
        maxVideoWidth,
        fixed,
        updateFloatingVideoSlot,
    ]);

    useEffect(() => {
        addFloatingVideoSlot(id, name, ref, fallback, fixed);
        return () => {
            removeFloatingVideoSlot(id);
        };
    }, [id, name, addFloatingVideoSlot, removeFloatingVideoSlot]);

    const onChange = useCallback(
        (intersecting, pctInViewport) => {
            if (!fixed) {
                updateFloatingVideoSlot(id, {
                    pctInViewport,
                });
            }
        },
        [id, updateFloatingVideoSlot, fixed],
    );

    // Do it only once per slot, pages sometime resize
    // weirdly after load and the resizeobserver misses it
    useEffect(() => {
        if (!fixed) {
            setTimeout(() => {
                setRefresh(1);
            }, 500);
        }
    }, [setRefresh, fixed]);

    const { metadata } = floatingVideo || {};
    const { width: videoWidth = null, height: videoHeight = null } = metadata || {};
    const finalRatio =
        isNumber(videoWidth) && isNumber(videoHeight) ? videoHeight / videoWidth : null;

    return (
        <Detector
            className={classNames([
                styles.container,
                {
                    [styles.fixed]: fixed,
                    [styles.vertical]: adaptative && finalRatio >= 1,
                    [styles.fullWidth]: fullWidth,
                    [styles.debug]: debug,
                    [className]: className !== null,
                },
            ])}
            threshold={[0, 0.25, 0.5, 0.75, 1]}
            onChange={onChange}
        >
            <div className={styles.content} ref={ref} />
        </Detector>
    );
};
FloatingVideoSlot.propTypes = propTypes;
FloatingVideoSlot.defaultProps = defaultProps;

export default FloatingVideoSlot;
